import React, { useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Panel, Collapse } from 'react-bootstrap';

import { useRequestData, useLabels } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { DropdownContainer } from 'wsm-dropdown';
import { setClassNames } from '@ddc/classnames-js';

import { transformUserRangeData } from '../utilities/facet-user-range';
import { isArrayEqual } from '../utilities/equal-array';
import { FACET_OVERRIDE_TYPE_SELECT, priceFacetIds } from '../global-constants';
import WebIntegrationPlaceholder from './WebIntegrationPlaceholder';
import Facet from './Facet';
import FacetTitle from './FacetTitle';
import FacetSkeleton from './FacetSkeleton';
import FacetButtonLink from './FacetButtonLink';
import FacetGeoLocationInline from './geo-location/FacetGeoLocationInline';
import StoreList from './geo-location/StoreList';

const FacetUserRange = React.lazy(() =>
	import(
		/* webpackChunkName: "FacetUserRange" , webpackPrefetch: true */ './user-range/FacetUserRange'
	)
);

const FacetGroup = ({
	facet,
	detailsLinkData,
	openDetailsModal,
	selectedValues,
	renderPanel = true,
	overrideType,
	includeExtras = true,
	isTopLevel = false
}) => {
	const {
		type,
		label,
		id: groupId,
		multiselect = undefined,
		values,
		collapsed
	} = facet || {};
	const inputType = multiselect ? 'checkbox' : 'radio';
	const { widgetName, windowId } = useRequestData();
	const [isCollapsed, setCollapsed] = useState(collapsed);
	const labels = useLabels();
	const geoLocationLabel = labels.get('GEO_LOCATION');
	let facetTitle = label;

	const isGeoLocationEnabled = !!window.DDC?.InvData?.getGeoData();

	const pageAlias = useSelector((state) => state?.moreRequestData?.pageAlias);

	// State derived from getAppliedFilters that represents currently selected facets
	const [userSelections, setUserSelections] = useState(selectedValues);

	useEffect(() => {
		setUserSelections(selectedValues);
	}, [selectedValues]);

	const updateFacets = (facetId, targetValue, event = null) => {
		const newSelectedIds = [];

		if (inputType === 'checkbox') {
			selectedValues.map((selected) =>
				newSelectedIds.push(selected.value)
			);
			// Remove selected value from array if already included
			const selectedIndex = newSelectedIds.indexOf(targetValue);
			if (selectedIndex !== -1) {
				newSelectedIds.splice(selectedIndex, 1);
			} else {
				// Add facet id to list of selected ids
				newSelectedIds.push(targetValue);
			}
			// Don't push anything to updatedSelections when the selection is ALL
		} else if (targetValue !== 'ALL') {
			newSelectedIds.push(targetValue);
		}

		window.DDC.InvData.setFilterSelections({
			filterName: facetId,
			filterValue: targetValue,
			filterValues: newSelectedIds,
			filterTitleText: facetTitle,
			isChip: false,
			isSelected: newSelectedIds.includes(targetValue),
			isSlider: false,
			element: event && event.target
		});

		setUserSelections(
			values.filter((selection) =>
				newSelectedIds.includes(selection.value)
			)
		);

		if (newSelectedIds.includes(targetValue)) {
			trackEvent(widgetName, windowId, {
				action: 'applied',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet added',
				dataToExcludeFromWidgetUserEvent: {
					facetId,
					facetTitle
				}
			});
		} else {
			trackEvent(widgetName, windowId, {
				action: 'removed',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet removed',
				dataToExcludeFromWidgetUserEvent: {
					facetId,
					facetTitle
				}
			});
		}
	};

	const handleDropdownChange = (option, facetId) => {
		const { selectedItem } = option;
		const { value } = selectedItem;
		updateFacets(facetId, value);
	};

	const handleChange = (event, facetId) => {
		const targetValue = event.target.value;
		updateFacets(facetId, targetValue, event);
	};

	/**
	 * Creates a list of Facet components for each FacetGroup
	 *
	 * @event handleChange
	 * @returns {Facet[] || FacetUserRange || null} Facet components
	 */

	const getFacets = () => {
		const handleSelect = (facetValue) => {
			let isSelected = !!userSelections.find(
				(selection) => selection.value === facetValue
			);
			if (!userSelections.length && facetValue === 'ALL') {
				isSelected = true;
			}
			return isSelected;
		};

		if (values?.length) {
			// TODO: Move this to configurable option in composer and facet-config
			if (overrideType && overrideType === FACET_OVERRIDE_TYPE_SELECT) {
				return (
					<DropdownContainer
						group={facet}
						onChange={handleDropdownChange}
						groupTitle={facetTitle}
						inputClass="border w-100 dropdown-toggle btn font-weight-normal text-left text-neutral-1000 bg-neutral-0"
					/>
				);
			}

			if (type === 'USER_RANGE') {
				const [facetValues, noPriceObj] = transformUserRangeData(
					facet,
					!!multiselect
				);
				if (facetValues) {
					return (
						<Suspense fallback={<FacetSkeleton />}>
							<FacetUserRange
								facetValues={facetValues}
								selectedValues={selectedValues}
								noPriceObj={noPriceObj}
								facetId={facet.id}
								isMultiselect={!!multiselect}
								facetTitle={facetTitle}
							/>
						</Suspense>
					);
				} else {
					return null;
				}
			}
			if (type === 'GEO') {
				facetTitle = geoLocationLabel;
				return <FacetGeoLocationInline facetTitle={facetTitle} />;
			}

			if (groupId === 'accountId' && isGeoLocationEnabled) {
				return (
					<StoreList
						stores={values}
						groupId={groupId}
						inputType={inputType}
						handleChange={handleChange}
						handleSelect={handleSelect}
						isTopLevel={isTopLevel}
					/>
				);
			}

			return (
				<ul className="list-unstyled line-height-condensed">
					{values?.map((item) => {
						return (
							<Facet
								facetLabel={item.label}
								inputType={inputType}
								facetValue={item.value}
								facetCount={item.count}
								groupId={groupId}
								isDisabled={false}
								onChange={handleChange}
								key={`${groupId}-${item.value}`}
								handleSelect={handleSelect}
							/>
						);
					})}
				</ul>
			);
		} else {
			return null;
		}
	};

	const [isInteracted, setInteracted] = useState(false);
	const handleCollapse = () => {
		setCollapsed(!isCollapsed);
		setInteracted(true);

		if (!isCollapsed) {
			trackEvent(widgetName, windowId, {
				action: 'collapsed',
				element: `${groupId} accordion`,
				result: 'Accordion collapsed'
			});
		} else {
			trackEvent(widgetName, windowId, {
				action: 'expanded',
				element: `${groupId} accordion`,
				result: 'Accordion expanded'
			});
		}
	};

	const onToggleCollapsed = () => {
		document
			.getElementById(
				`panel-collapse-${groupId}-${pageAlias}-${windowId}`
			)
			.removeAttribute('aria-expanded');
	};

	let facets = null;
	// Display request facets only when the panel is open
	// or user has previously toggled the panel
	// or if we're not rendering a panel
	if (!renderPanel || !isCollapsed || isInteracted) {
		facets = getFacets();

		if (!facets) {
			return null;
		}
	}

	const wiapiPlaceholder = priceFacetIds.includes(groupId) ? (
		<WebIntegrationPlaceholder
			location="search-facet-pricing"
			locationId={groupId}
		/>
	) : null;

	let hasDetails = detailsLinkData?.details;

	//	If normalFuelType facet contains only options :
	// 		Gasoline and Diesel
	// 		Gasoline and flexible
	// 		Diesel and Flexible
	//	Not show the about EV link
	if (groupId === 'normalFuelType' && values.length === 2) {
		const fuelMatches = [
			['Gasoline', 'Diesel'],
			['Gasoline', 'Flexible'],
			['Diesel', 'Flexible']
		];
		const fuelTypes = values?.map((fuelType) => fuelType?.value);
		const isOnlyGasOrDiesel = !!fuelMatches?.find((match) =>
			isArrayEqual(match, fuelTypes)
		);
		hasDetails = !isOnlyGasOrDiesel;
	}

	if (!renderPanel) {
		if (!includeExtras) {
			return <React.Fragment>{facets}</React.Fragment>;
		}
		return (
			<React.Fragment>
				{facets}
				{wiapiPlaceholder}
				{hasDetails && (
					<FacetButtonLink
						buttonText={detailsLinkData.aboutLink}
						onClick={() =>
							openDetailsModal(facet.id, null, detailsLinkData)
						}
					/>
				)}
			</React.Fragment>
		);
	}

	return (
		<Panel
			className={setClassNames([
				'facet-list-group',
				`${
					isGeoLocationEnabled && type === 'GEO'
						? 'p-0 border-bottom-0'
						: ''
				}`
			])}
			id={groupId}
			data-facet-group={groupId}
			expanded={!isCollapsed}
			onToggle={onToggleCollapsed}
		>
			<Panel.Heading role="presentation">
				<Panel.Title>
					<button
						type="button"
						aria-expanded={!isCollapsed}
						aria-controls={`panel-collapse-${groupId}-${pageAlias}-${windowId}`}
						id={`panel-title-${groupId}-${pageAlias}-${windowId}`}
						className="btn btn-unstyled w-100 p-0 border-0"
						onClick={handleCollapse}
					>
						<FacetTitle {...{ facetTitle, isCollapsed, groupId }} />
					</button>
				</Panel.Title>
			</Panel.Heading>
			<Collapse
				in={!isCollapsed}
				id={`panel-collapse-${groupId}-${pageAlias}-${windowId}`}
				role="region"
				aria-labelledby={`panel-title-${groupId}-${pageAlias}-${windowId}`}
				onEntered={onToggleCollapsed}
				onExited={onToggleCollapsed}
			>
				<div className="panel-collapse">
					<Panel.Body>
						{facets}
						{wiapiPlaceholder}
						{hasDetails && (
							<FacetButtonLink
								buttonText={detailsLinkData.aboutLink}
								onClick={() =>
									openDetailsModal(
										facet.id,
										null,
										detailsLinkData
									)
								}
							/>
						)}
					</Panel.Body>
				</div>
			</Collapse>
		</Panel>
	);
};

FacetGroup.propTypes = {
	facet: PropTypes.shape({
		type: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		isCurrency: PropTypes.bool,
		multiselect: PropTypes.bool,
		values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
		collapsed: PropTypes.bool.isRequired
	}).isRequired,
	detailsLinkData: PropTypes.shape({
		details: PropTypes.string,
		subText: PropTypes.string,
		aboutLink: PropTypes.string
	}),
	selectedValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
	renderPanel: PropTypes.bool,
	overrideType: PropTypes.string,
	openDetailsModal: PropTypes.func,
	includeExtras: PropTypes.bool,
	isTopLevel: PropTypes.bool
};

export default FacetGroup;
